import { HTMLAttributes, ReactNode, useContext, useEffect, useMemo, useRef } from 'react';
import { joinClassNames } from '../../utils';
import styles from './Drawer.module.scss';
import { CloseIcon } from '../Graphics';
import { CSSTransition } from 'react-transition-group';
import { AnimatedDiv } from '../../internalComponents/AnimatedDiv';
import { DrawerContext, DURATION } from './DrawerContext';
import { IconButton } from '../IconButton';
import { IconButtonSize, IconButtonVariant } from '../IconButton/enums';

type DrawerProps = {
  /** Is the slider currently open/displayed */
  isOpen: boolean;
  /** Function that will be called to close the slider */
  onClose: () => void;
  /**
   * Duration of transition in milliseconds
   * @default 300
   */
  transitionDuration?: number;
  children: ReactNode;
};

export const Drawer = ({ isOpen, onClose, transitionDuration, children }: DrawerProps) => {
  const nodeRef = useRef(null);
  useEffect(() => {
    const cb = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', cb);

    return () => document.removeEventListener('keydown', cb);
  }, [onClose]);

  const value = useMemo(
    () => ({
      isOpen,
      closeDrawer: onClose,
      transitionDuration: transitionDuration ?? DURATION,
    }),
    [isOpen, onClose, transitionDuration]
  );

  useEffect(() => {
    const cleanup = () => {
      const previousValue = document.body.dataset.previousOverflowYValue;
      if (previousValue !== undefined) {
        document.body.style.overflowY = previousValue;
        delete document.body.dataset.previousOverflowYValue;
      }
    };

    if (isOpen) {
      document.body.dataset.previousOverflowYValue = document.body.style.overflowY;
      document.body.style.overflowY = 'hidden';
    } else {
      cleanup();
    }

    return () => {
      cleanup();
    };
  }, [isOpen]);

  return (
    <DrawerContext.Provider value={value}>
      <CSSTransition
        mountOnEnter
        unmountOnExit
        appear
        in={isOpen}
        timeout={value.transitionDuration}
        nodeRef={nodeRef}
      >
        <div ref={nodeRef}>{children}</div>
      </CSSTransition>
    </DrawerContext.Provider>
  );
};

export const DrawerFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => {
  const classNames = joinClassNames(styles.footer, className);
  return <div className={classNames} {...props} />;
};

export const DrawerContent = ({ children, className, ...props }: HTMLAttributes<HTMLDivElement>) => {
  const { isOpen, transitionDuration } = useContext(DrawerContext);
  const nodeRef = useRef(null);

  return (
    <CSSTransition
      mountOnEnter
      unmountOnExit
      appear
      nodeRef={nodeRef}
      in={isOpen}
      timeout={transitionDuration}
      classNames={{
        appearDone: styles['drawer-visible'],
        appear: styles['drawer-visible'],
      }}
    >
      <AnimatedDiv
        role="dialog"
        aria-modal="true"
        className={joinClassNames(styles.drawer, className)}
        transitionDuration={transitionDuration}
        {...props}
        ref={nodeRef}
      >
        {children}
      </AnimatedDiv>
    </CSSTransition>
  );
};

export const DrawerToolbar = ({ children, className, ...props }: HTMLAttributes<HTMLDivElement>) => {
  const { closeDrawer } = useContext(DrawerContext);
  const classNames = joinClassNames(styles.toolbar, className);

  return (
    <div className={classNames} {...props}>
      {children}
      <div className={styles.spacer}></div>
      <IconButton
        title="close drawer"
        variant={IconButtonVariant.TransparentDark}
        size={IconButtonSize.Small}
        onClick={closeDrawer}
        icon={<CloseIcon className={styles.closeIcon} />}
      />
    </div>
  );
};

type DrawerOverlayProps = Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'onClick'>;
export const DrawerOverlay = ({ className, ...props }: DrawerOverlayProps) => {
  const nodeRef = useRef(null);
  const { isOpen, closeDrawer, transitionDuration } = useContext(DrawerContext);

  return (
    <CSSTransition
      mountOnEnter
      unmountOnExit
      appear
      nodeRef={nodeRef}
      in={isOpen}
      timeout={transitionDuration}
      classNames={{
        appearDone: styles['overlay-appear'],
        appear: styles['overlay-appear'],
      }}
    >
      <AnimatedDiv
        onClick={closeDrawer}
        className={joinClassNames(styles.overlay, className)}
        transitionDuration={transitionDuration}
        {...props}
        ref={nodeRef}
      />
    </CSSTransition>
  );
};
